// src/components/SharedLinksManager.jsx
// Modal component for viewing and managing shared album links
//
// FEATURES:
// - Lists all shared albums with metadata
// - Copy share URL to clipboard
// - Open share in new tab
// - Deactivate (delete) shares with confirmation
//
// USAGE:
// <SharedLinksManager open={isOpen} onClose={() => setIsOpen(false)} />

import React, { useState, useEffect, useCallback } from 'react';
import { getAllShares } from '../utils/shareStore';
import { deleteShare } from '../utils/share-v2';

// Toast notification helper
function showToast(message, type = 'info') {
  const colors = {
    info: '#3b82f6',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
  };
  
  const icons = {
    info: 'ℹ️',
    success: '✅',
    error: '❌',
    warning: '⚠️',
  };
  
  const toast = document.createElement('div');
  toast.style.cssText = `
    position: fixed;
    bottom: 100px;
    left: 50%;
    transform: translateX(-50%);
    background: ${colors[type]};
    color: white;
    padding: 12px 20px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.3);
    z-index: 99999;
    font-weight: 500;
    font-size: 14px;
    max-width: 90%;
    text-align: center;
    display: flex;
    align-items: center;
    gap: 8px;
  `;
  toast.innerHTML = `<span>${icons[type]}</span><span>${message}</span>`;
  document.body.appendChild(toast);
  
  setTimeout(() => {
    toast.style.opacity = '0';
    toast.style.transition = 'opacity 0.3s';
    setTimeout(() => toast.remove(), 300);
  }, 3000);
}

// Format date for display
function formatDate(timestamp) {
  if (!timestamp) return 'Unknown date';
  
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  }
}

// Individual share item component
function ShareItem({ share, onDelete, isDeleting }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(share.shareUrl);
      setCopied(true);
      showToast('Link copied to clipboard!', 'success');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast('Failed to copy link', 'error');
    }
  };
  
  const handleDelete = async () => {
    // Don't hide confirm UI - let the loading spinner show in the button
    await onDelete(share.shareId);
    // Only hide after delete completes (or fails)
    setShowConfirm(false);
  };
  
  return (
    <div className="bg-slate-800 rounded-xl p-4 border border-slate-700 hover:border-slate-600 transition-colors">
      <div className="flex items-start gap-4">
        {/* Thumbnail placeholder or icon */}
        <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-purple-600/30 to-blue-600/30 flex items-center justify-center flex-shrink-0 border border-slate-600">
          <span className="text-2xl">📸</span>
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white truncate text-base">
            {share.albumTitle || 'Untitled Album'}
          </h3>
          
          <div className="flex items-center gap-3 mt-1 text-sm text-slate-400">
            <span className="flex items-center gap-1">
              <span>📅</span>
              {formatDate(share.createdAt)}
            </span>
            {share.photoCount > 0 && (
              <span className="flex items-center gap-1">
                <span>📷</span>
                {share.photoCount} photos
              </span>
            )}
          </div>
          
          {/* Action buttons */}
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <button
              onClick={handleCopyLink}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors ${
                copied 
                  ? 'bg-emerald-600 text-white' 
                  : 'bg-slate-700 hover:bg-slate-600 text-white'
              }`}
            >
              {copied ? '✓ Copied!' : '📋 Copy Link'}
            </button>
            
            <a
              href={share.shareUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white font-medium flex items-center gap-1.5 transition-colors"
            >
              🔗 Open
            </a>
          </div>
        </div>
        
        {/* Delete button */}
        <div className="flex-shrink-0">
          {showConfirm ? (
            <div className="flex flex-col gap-2">
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="px-3 py-1.5 bg-red-600 hover:bg-red-700 rounded-lg text-sm text-white font-medium disabled:opacity-50 transition-colors"
              >
                {isDeleting ? (
                  <span className="flex items-center gap-1">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Deleting...
                  </span>
                ) : 'Confirm Delete'}
              </button>
              <button
                onClick={() => setShowConfirm(false)}
                disabled={isDeleting}
                className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm text-white font-medium disabled:opacity-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowConfirm(true)}
              className="px-3 py-1.5 bg-red-900/40 hover:bg-red-900/60 rounded-lg text-sm text-red-400 hover:text-red-300 font-medium flex items-center gap-1.5 transition-colors"
              title="Deactivate this shared link"
            >
              🗑️ Deactivate
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Main component
export default function SharedLinksManager({ open, onClose }) {
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  
  // Load shares when modal opens
  const loadShares = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const records = await getAllShares();
      setShares(records);
    } catch (err) {
      console.error('[SharedLinksManager] Failed to load shares:', err);
      setError('Failed to load shared links');
    }
    setLoading(false);
  }, []);
  
  useEffect(() => {
    if (open) {
      loadShares();
    }
  }, [open, loadShares]);
  
  // Handle delete
  const handleDelete = async (shareId) => {
    setDeletingId(shareId);
    try {
      const success = await deleteShare(shareId);
      if (success) {
        setShares(prev => prev.filter(s => s.shareId !== shareId));
        showToast('Share link deactivated successfully', 'success');
      } else {
        showToast('Failed to deactivate share link', 'error');
      }
    } catch (err) {
      console.error('[SharedLinksManager] Delete failed:', err);
      showToast('Failed to deactivate share link', 'error');
    }
    setDeletingId(null);
  };
  
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, onClose]);
  
  if (!open) return null;
  
  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className="relative bg-slate-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden border border-slate-700 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-700 flex-shrink-0">
          <div>
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <span>🔗</span>
              Shared Links
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Manage your shared album links
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition-colors"
            title="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ WebkitOverflowScrolling: 'touch' }}>
          {loading ? (
            <div className="text-center py-16">
              <div className="w-12 h-12 mx-auto mb-4">
                <svg className="animate-spin text-blue-500" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
              </div>
              <p className="text-slate-400">Loading shared links...</p>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <div className="text-4xl mb-4">😕</div>
              <p className="text-red-400 mb-4">{error}</p>
              <button
                onClick={loadShares}
                className="px-4 py-2 bg-slate-700 hover:bg-slate-600 rounded-lg text-white text-sm font-medium"
              >
                Try Again
              </button>
            </div>
          ) : shares.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-slate-800 flex items-center justify-center">
                <span className="text-4xl">🔗</span>
              </div>
              <h3 className="text-lg font-semibold text-white mb-2">No shared links yet</h3>
              <p className="text-slate-400 max-w-sm mx-auto">
                When you share an album as a story or preview, it will appear here so you can manage or deactivate it later.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats bar */}
              <div className="flex items-center justify-between text-sm text-slate-400 mb-2">
                <span>{shares.length} shared {shares.length === 1 ? 'album' : 'albums'}</span>
              </div>
              
              {/* Share list */}
              {shares.map(share => (
                <ShareItem
                  key={share.shareId}
                  share={share}
                  onDelete={handleDelete}
                  isDeleting={deletingId === share.shareId}
                />
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-700 bg-slate-800/50 flex-shrink-0">
          <div className="flex items-start gap-2 text-xs text-slate-500">
            <span className="flex-shrink-0">ℹ️</span>
            <p>
              <strong>Deactivating</strong> a link permanently removes the shared album from our servers. 
              Anyone with the link will no longer be able to view it. This action cannot be undone.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Export a button component for easy integration
export function SharedLinksButton({ className = '', onClick }) {
  const [count, setCount] = useState(0);
  
  // Load count on mount
  useEffect(() => {
    async function loadCount() {
      try {
        const { getShareCount } = await import('../utils/shareStore');
        const c = await getShareCount();
        setCount(c);
      } catch (err) {
        console.warn('Failed to load share count:', err);
      }
    }
    loadCount();
  }, []);
  
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-2 ${className}`}
      title="Manage shared links"
    >
      <span>🔗</span>
      <span>Shared Links</span>
      {count > 0 && (
        <span className="bg-blue-600 text-white text-xs px-1.5 py-0.5 rounded-full min-w-[20px] text-center">
          {count}
        </span>
      )}
    </button>
  );
}
